import React, { createContext, useContext, useState, useMemo } from 'react';
import { CustomerThemeTokens, CUSTOMER_DEFAULT_THEME, STORAGE_KEYS, tokensToCssVars } from './tokens';

interface CustomerThemeContextType {
  theme: CustomerThemeTokens;
  updateTheme: (partial: Partial<CustomerThemeTokens>) => void;
  resetTheme: () => void;
}

const CustomerThemeContext = createContext<CustomerThemeContextType>({
  theme: CUSTOMER_DEFAULT_THEME,
  updateTheme: () => {},
  resetTheme: () => {},
});

export const useCustomerTheme = () => useContext(CustomerThemeContext);

interface CustomerThemeProviderProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const CustomerThemeProvider: React.FC<CustomerThemeProviderProps> = ({
  children,
  className = '',
  style,
}) => {
  const [theme, setTheme] = useState<CustomerThemeTokens>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CUSTOMER_THEME);
      if (stored) {
        return { ...CUSTOMER_DEFAULT_THEME, ...JSON.parse(stored) };
      }
    } catch {
      // Fallback
    }
    return CUSTOMER_DEFAULT_THEME;
  });

  const updateTheme = (partial: Partial<CustomerThemeTokens>) => {
    setTheme((prev) => {
      const next = { ...prev, ...partial };
      try {
        localStorage.setItem(STORAGE_KEYS.CUSTOMER_THEME, JSON.stringify(next));
      } catch {
        // Fallback
      }
      return next;
    });
  };

  const resetTheme = () => {
    setTheme(CUSTOMER_DEFAULT_THEME);
    try {
      localStorage.removeItem(STORAGE_KEYS.CUSTOMER_THEME);
    } catch {
      // Fallback
    }
  };

  const cssStyle = useMemo(() => {
    return {
      ...tokensToCssVars(theme as unknown as Record<string, string>),
      ...style,
    };
  }, [theme, style]);

  return (
    <CustomerThemeContext.Provider value={{ theme, updateTheme, resetTheme }}>
      <div className={`customer-shell ${className}`} style={cssStyle}>
        {children}
      </div>
    </CustomerThemeContext.Provider>
  );
};
